import React, { KeyboardEvent, MouseEvent, useState } from 'react';
import { Button } from 'shared/Button/NewButton';
import { ProgressBar } from 'shared/ProgressBar';
import PlayButton from 'components/PlayButton';
import AudioPlayer from 'components/AudioPlayer';
import { getFormattedTimestamp } from 'modules/Time';
import { formatBytes } from 'modules/NumberFormatter';
import { EpisodeAudios } from 'modules/AnchorAPI/v3/episodes/fetchEpisodeOverview';
import {
  calculateAggregateDuration,
  calculateTotalDuration,
} from 'screens/EpisodeOptionsScreen/components/EpisodeContent/utils';
import {
  uploaderCancelButtonStyle,
  UploaderContainer,
  UploaderLabelsContainer,
  UploaderStatusContainer,
  UploaderStatusHeading,
  UploaderStatusLabel,
} from '../styles';
import {
  MediaPreviewContentWrapper,
  MediaPreviewPlayerWrapper,
  ProgressBarContainerButton,
  uploaderEditEpisodeButtonStyle,
  VideoDisclaimerWrapper,
} from './styles';

type MediaPreviewStateProps = {
  episodeAudios: EpisodeAudios[];
  isVideo: boolean;
  title: string;
  webEpisodeId: string;
  totalSize: number;
  onClickDelete: () => void;
};

export const MediaPreviewState = ({
  episodeAudios,
  isVideo,
  title,
  webEpisodeId,
  onClickDelete,
  totalSize,
}: MediaPreviewStateProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [doSeek, setDoSeek] = useState(false);

  // Playback positions are in seconds
  const [playbackPosition, setPlaybackPosition] = useState(0);
  const [localPlaybackPosition, setLocalPlaybackPosition] = useState(0);
  const [activeAudioIndex, setActiveAudioIndex] = useState(0);

  const playableEpisodeAudios = episodeAudios.filter(
    audio => audio.type !== 'ad'
  );

  const playableAudioDuration = calculateTotalDuration(playableEpisodeAudios);
  const aggregateDuration = calculateAggregateDuration(playableEpisodeAudios);
  const findAudioIndex = (playbackPos: number) =>
    aggregateDuration.findIndex(e => e > playbackPos);

  /**
   * Returns the timestamp (in s) of the current position within the
   * episodeAudio segment denoted by audioIndex.
   */
  const getLocalPlaybackPosition = (
    audioIndex: number,
    globalPlaybackPosition: number
  ) => {
    return (
      globalPlaybackPosition -
      (audioIndex === 0 ? 0 : aggregateDuration[audioIndex - 1])
    );
  };

  /**
   * Returns the timestamp (in s) of the current position within the entire
   * episode (all the segments in episodeAudios).
   */
  const getGlobalPlaybackPosition = (
    audioIndex: number,
    activeAudioPlaybackPosition: number
  ) => {
    const previousAudiosLength =
      audioIndex === 0 ? 0 : aggregateDuration[audioIndex - 1];

    return previousAudiosLength + activeAudioPlaybackPosition;
  };

  const onPlayOrPause = () => {
    setIsPlaying(!isPlaying);
  };

  const onProgressBarClick = (e: MouseEvent<HTMLElement>) => {
    const { width, left } = e.currentTarget.getBoundingClientRect();
    const clickedPositionTime =
      ((e.clientX - left) / width) * (playableAudioDuration / 1000);
    setDoSeek(true);
    setPlaybackPosition(clickedPositionTime);
    const newActiveAudioIndex = findAudioIndex(clickedPositionTime);
    setActiveAudioIndex(newActiveAudioIndex);
    setLocalPlaybackPosition(
      getLocalPlaybackPosition(newActiveAudioIndex, clickedPositionTime)
    );
    if (!isPlaying) {
      setIsPlaying(true);
    }
  };

  const onProgressBarKeyDown = (e: KeyboardEvent) => {
    switch (e.keyCode) {
      case 32:
      case 13:
        onPlayOrPause();
    }
  };

  const handlePlayNextSegment = () => {
    const lastAudioIndex = playableEpisodeAudios.length - 1;
    if (activeAudioIndex === lastAudioIndex) {
      setIsPlaying(false);
      setPlaybackPosition(0);
      setLocalPlaybackPosition(0);
      setActiveAudioIndex(0);
    } else {
      const nextIndex = activeAudioIndex + 1;
      setPlaybackPosition(getGlobalPlaybackPosition(nextIndex, 0));
      setActiveAudioIndex(nextIndex);
      setLocalPlaybackPosition(0);
      setIsPlaying(true);
    }
  };

  return (
    <MediaPreviewContentWrapper>
      <MediaPreviewPlayerWrapper>
        {playableEpisodeAudios.map((audio: EpisodeAudios, i: number) => {
          return (
            <AudioPlayer
              actions={{
                setDoSeek: (isSeeking: boolean) => {
                  setDoSeek(isSeeking);
                },
                updatePlaybackPosition: (playbackPositionInSeconds: number) => {
                  setPlaybackPosition(
                    getGlobalPlaybackPosition(
                      activeAudioIndex,
                      playbackPositionInSeconds
                    )
                  );
                },
              }}
              playbackPosition={localPlaybackPosition}
              doSeek={doSeek}
              doPreload={true}
              isActive={i === activeAudioIndex}
              isPlaying={isPlaying}
              onEnded={() => {
                handlePlayNextSegment();
              }}
              onPlayOrPause={onPlayOrPause}
              key={`audio-player-${audio.webAudioId}-${aggregateDuration[i]}`}
              url={audio.url}
            />
          );
        })}
        <UploaderContainer>
          <UploaderContainer>
            <PlayButton
              isPlaying={isPlaying}
              size={46}
              colors={{ colorClass: 'black' }}
              onClick={onPlayOrPause}
              ariaLabel="Play episode audio"
            />
            <UploaderLabelsContainer>
              <UploaderStatusHeading>{title}</UploaderStatusHeading>
              <UploaderStatusLabel>
                {formatBytes(totalSize)} |{' '}
                {getFormattedTimestamp(playableAudioDuration, {
                  omitZeroHours: true,
                  roundMilliseconds: true,
                })}
              </UploaderStatusLabel>
            </UploaderLabelsContainer>
          </UploaderContainer>
          <UploaderStatusContainer>
            <Button
              color="white"
              className={uploaderCancelButtonStyle}
              onClick={onClickDelete}
              type="button"
            >
              Delete
            </Button>
            {!isVideo && (
              <Button
                color="white"
                className={uploaderEditEpisodeButtonStyle}
                kind="link"
                href={`/dashboard/episode/${webEpisodeId}/edit`}
              >
                Edit your audio
              </Button>
            )}
          </UploaderStatusContainer>
        </UploaderContainer>
        <ProgressBarContainerButton
          onClick={onProgressBarClick}
          onKeyDown={onProgressBarKeyDown}
          type="button"
        >
          <ProgressBar
            percentage={Math.round(
              (playbackPosition / (playableAudioDuration / 1000)) * 100
            )}
            height={4}
            aria-label="audio seek bar"
          />
        </ProgressBarContainerButton>
      </MediaPreviewPlayerWrapper>
      {isVideo && (
        <VideoDisclaimerWrapper>
          Anchor does not currently support video previews, but your video will
          be available on Spotify.
        </VideoDisclaimerWrapper>
      )}
    </MediaPreviewContentWrapper>
  );
};
